import React, { useState } from 'react';

import { PictureUpload, usePicturesContext } from './PicturesContext';
import { InternalQuestion } from './SubmissionContext';

type ImageGalleryContextType = {
  isShowingImageGallery: boolean;
  setIsShowingImageGallery: (boolean) => void;
  getGalleryPictures: (question: InternalQuestion) => {
    pictures: PlaceholderWithPicture[];
    genericPictures: PlaceholderWithPicture[];
    index: number;
  };
  getPlaceholdersWithoutPicture: (question: InternalQuestion) => PlaceholderWithPicture[];
  setGallerySelectedPicture: (picture: PlaceholderWithPicture) => void;
  gallerySelectedPicture: PlaceholderWithPicture;
  getGenericPictures: (question: InternalQuestion) => PlaceholderWithPicture[];
};

const ImageGalleryContext = React.createContext<ImageGalleryContextType>(null);

export type PlaceholderWithPicture = {
  placeholderName: string;
  placeholderUrl: string;
  placeholderUrlForCamera: string;
  picture: PictureUpload | null;
};

export const ImageGalleryProvider = ({ children }) => {
  const [isShowingImageGallery, setIsShowingImageGallery] = useState<boolean>(false);
  const [gallerySelectedPicture, setGallerySelectedPicture] = useState<PlaceholderWithPicture | null>(null);
  const { pictures } = usePicturesContext();

  /**
   * Given the question.imagePlaceholders and pictures
   * from PicturesContext, this function associates placeholders
   * with pictures.
   *
   * If a picture in pictures does not have a placeholder associated to it,
   * then consider it a GENERIC picture.
   *
   * GENERIC pictures are render after all pictures with a NON-GENERIC placeholder
   * are rendered.
   *
   * It returns the pictures for the gallery (placeholders with pictures associated to it),
   * the genericPictures which is an array of all the pictures with a 'GENERIC' placeholder
   * and the index of the last placeholder that has a picture associated to it.
   *
   * This last index helps in rendering a gallery until this last position and not all placeholders
   * @param question
   * @returns
   */
  const getGalleryPictures = (question: InternalQuestion) => {
    const questionId = question.key;
    const galleryPictures = pictures.filter((picture) => picture.questionId === questionId);

    const placeholdersWithPictures: PlaceholderWithPicture[] = [];

    /**
     * Merge placeholders so when given a placeholder name
     * we can get placeholderUrl (for gallery question) and
     * placeholderUrlForCamera (for placeholders when camera is opened)
     */
    const placeholdersMap = new Map();
    question.imagesPlaceholders?.forEach((imagePlaceholder) =>
      placeholdersMap.set(imagePlaceholder.name, {
        placeholderName: imagePlaceholder.name,
        placeholderUrl: imagePlaceholder.url,
      }),
    );

    question.imagesPlaceholdersForCamera?.forEach((imagePlaceholderForCamera) => {
      const name = imagePlaceholderForCamera.name;
      placeholdersMap.set(name, {
        ...placeholdersMap.get(name),
        placeholderUrlForCamera: imagePlaceholderForCamera.url,
      });
    });

    const mergedPlaceholders: PlaceholderWithPicture[] = Array.from(placeholdersMap.values());

    mergedPlaceholders?.forEach((placeholder) => {
      placeholdersWithPictures.push({
        picture: null,
        placeholderName: placeholder.placeholderName,
        placeholderUrl: placeholder.placeholderUrl,
        placeholderUrlForCamera: placeholder.placeholderUrlForCamera,
      });
    });

    const genericPictures: PlaceholderWithPicture[] = [];
    galleryPictures.forEach((galleryPicture) => {
      const index = placeholdersWithPictures.findIndex(
        (placeholderWithPicture) => placeholderWithPicture.placeholderName === galleryPicture.placeholderName,
      );

      if (index !== -1) {
        placeholdersWithPictures[index].picture = galleryPicture;
      } else {
        genericPictures.push({
          picture: galleryPicture,
          placeholderName: galleryPicture.placeholderName,
          placeholderUrl: '',
          placeholderUrlForCamera: '',
        });
      }
    });

    const result = [...placeholdersWithPictures, ...genericPictures];
    let lastIndexWithPicture = -1;
    result.forEach((placeholderWithPicture, index) => {
      if (placeholderWithPicture.picture) lastIndexWithPicture = index;
    });

    return {
      pictures: result,
      genericPictures,
      index: lastIndexWithPicture,
    };
  };

  /**
   * Given a question, it returns all pictures for that question
   * that have a 'GENERIC' placeholder
   * @param question
   * @returns
   */
  const getGenericPictures = (question: InternalQuestion) => {
    const questionId = question.key;
    return pictures
      .filter((picture) => picture.questionId === questionId && picture.placeholderName?.includes('GENERIC'))
      .map((picture) => {
        const placeholder: PlaceholderWithPicture = {
          picture: picture,
          placeholderName: picture.placeholderName,
          placeholderUrl: '',
          placeholderUrlForCamera: '',
        };

        return placeholder;
      });
  };

  /**
   * This function helps in getting placeholders that still need a
   * picture.
   *
   * A view like 'Camera' can use it to calculate the next picture to be
   * taken.
   * @param question
   * @returns
   */
  const getPlaceholdersWithoutPicture = (question: InternalQuestion) => {
    const { pictures: placeholdersWithPictures } = getGalleryPictures(question);
    return placeholdersWithPictures.filter((placeholderWithPicture) => placeholderWithPicture.picture === null);
  };

  return (
    <ImageGalleryContext.Provider
      value={{
        isShowingImageGallery,
        setIsShowingImageGallery,
        getGalleryPictures,
        getPlaceholdersWithoutPicture,
        setGallerySelectedPicture,
        gallerySelectedPicture,
        getGenericPictures,
      }}
    >
      {children}
    </ImageGalleryContext.Provider>
  );
};

export function useImageGalleryContext() {
  return React.useContext(ImageGalleryContext);
}
